import type { SingleValueProps } from 'react-select';

import { Select } from '@/_components';
import AgentIcon from '@/_shared/images/icons/agent.svg?react';
import TicketStatusIcon from '@/_shared/images/icons/ticket-status.svg?react';
import type { SvgIconProps } from '@/types';

type FilterOption = {
  value: string;
  label: string;
};

type IconType = 'user' | 'status';

type FilterDropdownProps = {
  value?: FilterOption | null;
  label?: string;
  options: FilterOption[];
  onSelect?: (option: FilterOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
  getOptionLabel?: (option: FilterOption) => React.ReactNode;
  showIcon?: IconType;
};

const iconMap: Record<IconType, SvgIconProps> = {
  user: AgentIcon,
  status: TicketStatusIcon,
};

const DropdownIcon = ({ iconType }: { iconType?: IconType }) => {
  if (!iconType) return null;

  const Svg = iconMap[iconType];

  return <Svg className="text-c-card-muted mr-2 h-3 min-h-3 w-3 min-w-3" />;
};

const CustomDropdownIndicator = () => <img src="/images/icons/downArrow.svg" alt="" className="h-3 w-3 opacity-60" />;

export function FilterDropdown({
  value,
  options,
  onSelect,
  disabled = false,
  placeholder = 'Select...',
  getOptionLabel,
  label,
  showIcon,
}: FilterDropdownProps) {
  const customGetOptionLabel =
    getOptionLabel ||
    ((option) => (
      <div className="flex items-center">
        <DropdownIcon iconType={showIcon} />
        <span className="overflow-hidden text-ellipsis">{option.label}</span>
      </div>
    ));

  const customSingleValue = ({ children, ...props }: SingleValueProps<FilterOption>) => (
    <div {...props} className="flex items-center text-xs">
      <DropdownIcon iconType={showIcon} />
      <span className="overflow-hidden text-ellipsis">{children}</span>
    </div>
  );

  return (
    <Select
      label={label}
      value={value}
      options={options}
      onChange={(newValue) => onSelect?.(newValue as FilterOption | null)}
      disabled={disabled}
      isClearable={false}
      placeholder={placeholder}
      getOptionLabel={customGetOptionLabel}
      getOptionValue={(option) => option.value}
      noBorder
      className="h-6"
      containerClassName="max-w-32 p-0"
      valueContainerClassName="p-0!"
      singleValueClassName="text-xs"
      components={{
        SingleValue: customSingleValue,
        DropdownIndicator: CustomDropdownIndicator,
        IndicatorSeparator: () => null,
      }}
    />
  );
}
